import { useEffect, useRef } from 'react';

/**
 * Binds a throttled event handler to only execute when the browser is ready to paint the next frame.
 *
 * @param {String} eventName The name of the event to bind the handler to
 * @param {Function} handler The function to execute when the event is called
 * @param {Object} [options]
 * @param {DOMElement} [options.element=window] What element to bind handler to
 * @param {Boolean} [options.callOnInit=false] If callback should be called on initial component initialization
 */
const useThrottledHandler = (eventName, handler, options = {}) => {
  const { element = false, callOnInit = false } = options;
  const savedHandler = useRef();
  const ticking = useRef();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const currentElement = element || window;
    const isSupported = currentElement && currentElement.addEventListener;

    if (!isSupported) {
      return () => { };
    }

    const eventListener = event => savedHandler.current(event);

    if (callOnInit) {
      eventListener();
    }

    currentElement.addEventListener(eventName, () => {
      if (ticking.current) {
        window.cancelAnimationFrame(ticking.current);
      }

      ticking.current = window.requestAnimationFrame(eventListener);
    });

    return () => {
      window.cancelAnimationFrame(ticking.current);
      currentElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler, callOnInit]);
};

export default useThrottledHandler;
